import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Tabs,
  Input,
  Spin,
  Form,
  Tooltip,
  Radio,
  Checkbox,
  Tag,
  Button,
  Alert,
  Popconfirm,
  notification,
  Row,
  Col,
  Select,
} from 'antd';
import DatePicker from '../DatePicker';
import { QuestionCircleOutlined, PlusCircleOutlined, ThunderboltOutlined, DownCircleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash.debounce';
import {
  validSendEmail,
  validPatientRecipient,
  validDOB,
  validFaxNumber,
  validPartialNumber,
  formatDate,
  formatValidFaxNumber,
} from '../../global/validation';
import { verifyFeatureAccess } from '../../global/featureFlags';
import DraggableModal from '../modal/DraggableModal';
import { getDistributionLists } from '../../actions/userProfile';
import { captureAnalytics } from '../../actions/preload';
import { makeSingleFlight } from '../../utils/singleFlight';

const { TabPane } = Tabs;

const DOCUMENT_ACCESS_DURATION_DAYS = {
  medical_record: 30,
  claim: 60,
  medical_record_90_days: 90,
  medical_record_120_days: 120,
};

const customContentStyle = {
  width: '60%',
};

// Add positioning to the loader
const inputBox = {
  display: 'flex',
  alignItems: 'baseline',
  flexFlow: 'row nowrap',
};

const dropDownStyle = {
  width: 300,
  left: -20,
};

// Theme for react-autosuggest and styling for sub components
// https://github.com/moroshko/react-autosuggest#theme-optional
const theme = {
  container: {
    position: 'relative',
  },
  input: {
    width: '600px',
    marginTop: '10px',
  },
  inputOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  suggestionsContainer: {
    display: 'none',
  },
  suggestionsContainerOpen: {
    display: 'block',
    position: 'absolute',
    width: 600,
    border: '1px solid #aaa',
    backgroundColor: '#fff',
    fontFamily: 'Helvetica, sans-serif',
    fontWeight: 300,
    fontSize: 13,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    maxHeight: 150,
    overflowY: 'auto',
    zIndex: 2,
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  suggestion: {
    cursor: 'pointer',
    padding: '10px 20px',
  },
  suggestionHighlighted: {
    backgroundColor: '#ddd',
  },
  sectionContainer: {
    borderTop: '1px solid #ccc',
  },
  sectionTitle: {
    padding: '10px 0 0 10px',
    fontSize: 13,
    color: '#777',
  },
  sectionContainerFirst: {
    borderTop: '0',
  },
};

const sendContainer = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

const inputMaxText = 'Subject should have a maximum of 150 characters';

// Helper function to parse full name into first, middle, last
function parsePatientName(fullName) {
  if (!fullName || fullName.trim() === '') {
    return { firstName: '', middleName: '', lastName: '' };
  }

  const nameParts = fullName.trim().split(/\s+/); // Split by whitespace

  if (nameParts.length === 1) {
    return { firstName: nameParts[0], middleName: '', lastName: '' };
  }
  if (nameParts.length === 2) {
    return { firstName: nameParts[0], middleName: '', lastName: nameParts[1] };
  }
  // 3 or more parts: first is firstName, last is lastName, and everything in between is middleName
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  const middleName = nameParts.slice(1, -1).join(' ');
  return { firstName, middleName, lastName };
}

// Exported unconnected for tests (e.g. the double-submit guard regression) so the bare
// class methods can be exercised without the connect() + Redux store wiring, mirroring
// AssignedRecords/ReceivedRecords in earlier phases of the double-submit sweep.
export class SendRecordsDialog extends Component {
  constructor(props) {
    super();
    // Determine name fields based on what's available
    let firstName = '';
    let middleName = '';
    let lastName = '';
    let nameValid = false;

    if (props.patientFirstName || props.patientLastName) {
      // Use separate name fields if available
      firstName = props.patientFirstName || '';
      middleName = props.patientMiddleName || '';
      lastName = props.patientLastName || '';
      nameValid = firstName !== '' && lastName !== '';
    } else if (props.patientName) {
      // Parse the full name
      const parsed = parsePatientName(props.patientName);
      firstName = parsed.firstName;
      middleName = parsed.middleName;
      lastName = parsed.lastName;
      nameValid = firstName !== '' && lastName !== '';
    }

    this.state = {
      open: props.open,
      // only set it to sender information if user clicked reply
      contact: '',
      org: '',
      subject: '',
      inputError: 'Enter a valid 10 digit fax number or email address',
      hasDuplicateRecipient: false,
      isProviderValid: true,
      emailValid: false,
      patientRecipientValid: false,
      patientDOBValid: false,
      patientNameValid: nameValid,
      faxNumberValid: false,
      isChecked: true,
      isPatient: false,
      signatureRequired: false,
      patient: {
        recipient: '',
        firstName,
        middleName,
        lastName,
        dob: props.patientDOB || '',
      },
      recipients: [],
      value: '',
      multipleProviderRecipients: [],
      patientValue: '',
      documentType: 'medical_record',
      extendedDocumentAccessDurationsEnabled: false,
      callerId: !props.defaultFaxNumber ? null : props.defaultFaxNumber,
      removeCoversheet: false,
      showRemoveCoversheetCheckbox: false,
      // only set it to sender information if user clicked reply
      providerRecipientValue: props.isReplyButtonClicked ? (props.senderContactReply ? props.senderContactReply : '') : '',
      hasSingleValidEmailRecipient: false,
      hasSingleValidFaxRecipient: false,
      allowPatientEdits: true,
      showConfirmUpdatePatientInfo: false,
      selectedRecord: props.selectedRecord,
      // only used when user hits reply in the record viewer
      senderContactReply: '',
      noReplyInformationError: false, // used to display error message when user tries to reply to record sent to himself
      disableSecureReplies: false,
      scheduleFaxForLater: false,
      scheduledDateTime: null,
      isDistributionList: false,
      formattedDistributionLists: [],
      distributionListSlug: null,
      distributionListValue: '',
      filteredDistributionLists: [],
      isLoadingDistributionLists: false,
      distributionListsError: null,
    };

    // Wait a half a second before making request.
    // Ensures the function does not fire on every key stroke
    this.debouncedLoadSuggestions = debounce(this.loadSuggestions, 500);

    // Debounce the medsender user check to prevent excessive API calls
    // leading: true fires immediately on first valid input
    // trailing: true fires again after 500ms if still typing
    // We cancel the debounce when input becomes invalid so next valid input fires immediately
    this.debouncedCheckMedsenderUser = debounce(this.checkMedsenderUserOnInput, 500, { leading: true, trailing: true });

    // Ref for record info form
    this.recordInfoFormRef = React.createRef(null);
    
    // Placeholder messages for different input types
    this.placeholderMap = {
      patient: 'Patient Email Address or Cell Phone Number',
      distributionList: 'Enter Distribution List Name'
    };

    // To track API request status
    this._isDistributionListRequestInProgress = false;

    // Single-flight guard so a fast double-click on Send (or the Popconfirm confirm
    // path) can't fire two real sends.
    this.submit = makeSingleFlight((...args) => this.runSubmit(...args));
  }

  async componentDidMount() {
    const { senderContactReply, isReplyButtonClicked, senderOrganizationReply, senderNameReply } = this.props;
    const [showRemoveCoversheetCheckbox, extendedDocumentAccessDurationsEnabled] = await Promise.all([
      verifyFeatureAccess('hide_coversheet'),
      verifyFeatureAccess('extended_document_access_durations'),
    ]);
    this.setState({
      showRemoveCoversheetCheckbox,
      extendedDocumentAccessDurationsEnabled,
    });
    if (isReplyButtonClicked) {
      if (senderContactReply) {
        const isValidFaxNumber = validFaxNumber(senderContactReply).valid;
        const isValidEmail = validSendEmail(senderContactReply);
        this.setState({
          hasDuplicateRecipient: false,
          isProviderValid: true,
          org: senderOrganizationReply,
          contact: senderNameReply,
        });
        if (isValidFaxNumber) {
          this.setState({
            hasSingleValidFaxRecipient: true,
          });
        } else {
          this.setState({
            hasSingleValidEmailRecipient: true,
          });
        }
        // Trigger medsender user check for prefilled reply recipient
        if (isValidFaxNumber || isValidEmail) {
          const faxNumberOrEmail = isValidFaxNumber
            ? formatValidFaxNumber(senderContactReply, 'Domestic')
            : senderContactReply;
          const recipientType = isValidFaxNumber ? 'fax' : 'email';
          this.checkIfMedsenderUser(faxNumberOrEmail, recipientType);
        }
      } else {
        this.setState({ noReplyInformationError: true })
      }
    }

    this.addScrollListener();
    this.setScrollButtonPosition();
  }

  componentWillUnmount() {
    this.removeScrollListener();
    // Cancel pending debounced check to prevent setState on unmounted component
    this.debouncedCheckMedsenderUser.cancel();
  }

  componentDidUpdate(prevProps) {
    if (this.state.open !== prevProps.open) {
      this.setScrollButtonPosition();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.open !== nextProps.open) {
      this.setState({
        open: nextProps.open,
      });
    }

    // If new search results come in update the state
    if (this.state.recipients !== nextProps.dataSource) {
      this.setState({
        recipients: nextProps.dataSource,
      });
    }

    const {
      patientDOB, patientFirstName, patientMiddleName, patientLastName, patientName,
    } = this.props;

    // Check if the selected record has changed (comparing record IDs)
    const recordChanged = this.state.selectedRecord !== nextProps.selectedRecord;

    // Handle name fields updates - check if any name-related props have changed
    const nameFieldsChanged = patientFirstName !== nextProps.patientFirstName
     || patientMiddleName !== nextProps.patientMiddleName
     || patientLastName !== nextProps.patientLastName
     || patientName !== nextProps.patientName;

    // Update name fields when props change, but preserve user input (unless record has changed)
    if (nameFieldsChanged || recordChanged) {
      let firstName = '';
      let middleName = '';
      let lastName = '';
      let nameValid = false;

      if (nextProps.patientFirstName || nextProps.patientLastName) {
        firstName = nextProps.patientFirstName || '';
        middleName = nextProps.patientMiddleName || '';
        lastName = nextProps.patientLastName || '';
        nameValid = firstName !== '' && lastName !== '';
      } else if (nextProps.patientName) {
        const parsed = parsePatientName(nextProps.patientName);
        firstName = parsed.firstName;
        middleName = parsed.middleName;
        lastName = parsed.lastName;
        nameValid = firstName !== '' && lastName !== '';
      }

      this.setState((prevState) => {
        // Update from props if: local state is empty or selected record has changed
        const shouldUpdateFromProps = (!prevState.patient.firstName && !prevState.patient.lastName) || recordChanged;
        return {
          patientNameValid: shouldUpdateFromProps ? nameValid
            : (prevState.patient.firstName && prevState.patient.lastName),
          patient: {
            ...prevState.patient,
            firstName: shouldUpdateFromProps ? firstName : prevState.patient.firstName,
            middleName: shouldUpdateFromProps ? middleName : prevState.patient.middleName,
            lastName: shouldUpdateFromProps ? lastName : prevState.patient.lastName,
          },
          // Update the tracked selectedRecord
          selectedRecord: nextProps.selectedRecord,
        };
      });
    }

    // Patient DOB handling - update if blank, props changed, or record changed
    const dobChanged = patientDOB !== nextProps.patientDOB;

    if (!this.state.patient.dob || dobChanged || recordChanged) {
      this.setState((prevState) => {
        const shouldUpdateDOB = !prevState.patient.dob || recordChanged || dobChanged;

        return {
          patientDOBValid: shouldUpdateDOB ? validDOB(nextProps.patientDOB) : prevState.patientDOBValid,
          patient: {
            ...prevState.patient,
            dob: shouldUpdateDOB ? nextProps.patientDOB : prevState.patient.dob,
          },
        };
      });
    }

    // Update selectedRecord if it changed but wasn't updated in the name/DOB handlers above
    if (recordChanged && !nameFieldsChanged && !dobChanged) {
      this.setState({
        selectedRecord: nextProps.selectedRecord,
      });
    }

    if (!this.state.isPatient) {
      this.setState({ patientValue: '' });
    }
  }

  handleDocumentTypeChange = (event) => {
    const { value } = event.target;
    captureAnalytics('document_access_duration_selected', {
      document_access_days: DOCUMENT_ACCESS_DURATION_DAYS[value],
    });
    this.setState({ documentType: value });
  };

  addScrollListener = () => {
    this.modalBodyElement = document.querySelector('.send-records-modal .react-draggable');
    if (this.modalBodyElement) {
      this.modalBodyElement.addEventListener('scroll', this.handleScroll);
      this.handleScroll();
    }
  }
  
  removeScrollListener = () => {
    if (this.modalBodyElement) {
      this.modalBodyElement.removeEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll = () => {
    const modalBody = document.querySelector('.send-records-modal .react-draggable');

    if (modalBody) {
      // Add small buffer (1px) to account for potential decimal values in scroll calculations
      if (modalBody.scrollTop + modalBody.clientHeight >= modalBody.scrollHeight - 1) {
        this.setState({
          scrollAtBottom: true,
        });
      } else {
        this.setState({
          scrollAtBottom: false,
        });
      }
    }
  }

  setScrollButtonPosition = () => {
    if (this.state.isPatient || this.state.isDistributionList) {
      return;
    }
    // Set position of the scroll to bottom icon
    const modalBody = document.querySelector('.send-records-modal .react-draggable');
    if (modalBody) {
      const modalPosition = modalBody.getBoundingClientRect();
      this.setState({
        iconPosition: {
          x: modalPosition.right - 72,
          y: modalPosition.bottom - 72,
        },
      });
    }
  }

  scrollToBottom = () => {
    const modalBody = document.querySelector('.send-records-modal .react-draggable');
    if (modalBody) {
      modalBody.scrollTo({
        top: modalBody.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  // Store input reference for the patient field.
  storePatientInputReference = (autosuggest) => {
    if (autosuggest !== null) {
      this.patientInput = autosuggest.input;
    }
  }

  storeDistributionListInputReference = (autosuggest) => {
    if (autosuggest !== null) {
      this.distributionListInput = autosuggest.input;
    }
  }

  // Store input reference for the provider field.
  storeInputReference = (autosuggest) => {
    if (autosuggest !== null) {
      this.input = autosuggest.input;
    }
  }

  // Autosuggest calls this function when the user interacts with the text input
  // by:
  // a) Typing something
  // b) Focusing on the input
  // c) something else, documented here: https://git.io/fNQ4P
  onSuggestionsFetchRequested = ({ value }) => {
    // Only fetch suggestions if text in input has changed
    // This avoids API calls when the input is focused with the same value
    const { value: oldValue, isPatient, patient } = this.state;
    const fetchNewSuggestion = (value.trim() !== oldValue.trim());
    if (fetchNewSuggestion) {
      if (isPatient) {
        // Only validate the recipient field being typed in, not the name fields
        // Name validation is handled separately in patientMeta when name fields change
        this.setState({
          patientRecipientValid: validPatientRecipient(value),
        });
      } else {
        const { valid: faxNumberValid } = validFaxNumber(value);
        this.setState({
          faxNumberValid,
          emailValid: validSendEmail(value),
          org: '',
          contact: '',
        });
      }
      // Allow search for partial fax numbers with special characters e.g 999-080-
      const { valid: partialNumberValid, rawNumber: number } = validPartialNumber(value);

      if (partialNumberValid) {
        return this.debouncedLoadSuggestions(number);
      }
      return this.debouncedLoadSuggestions(value);
    }
  };

  // Updates the state when recipient is typed in
  onChange = (event, { newValue }) => {
    if (this.state.isPatient) {
      this.setState((prevState) => {
        return {
          patientRecipientValid: validPatientRecipient(newValue),
          patientValue: newValue,
          patient: {
            ...prevState.patient,
            recipient: newValue,
          },
        };
      });
    } else if (this.state.isDistributionList) {
      this.setState({ 
        distributionListValue: newValue,
        // Autocomplete distribution lists based on the inputted name
        filteredDistributionLists:
          [{
            recipients: this.state.formattedDistributionLists[0]?.recipients.filter(distributionList => 
              distributionList.recipient.toLowerCase().includes(newValue.toLowerCase())
            ) || []
          }]
      });
    } else {
      const { multipleProviderRecipients } = this.state;
      const trimmedInput = newValue.trim();
      const isValidFaxNumber = validFaxNumber(trimmedInput).valid;
      const isValidEmail = validSendEmail(trimmedInput);
      const isValidFaxNumberOrEmail = isValidEmail || isValidFaxNumber;
      let singleValidInputFieldRecipient = isValidFaxNumberOrEmail;
      if (singleValidInputFieldRecipient) {
        if (isValidFaxNumber) {
          singleValidInputFieldRecipient = 'hasSingleValidFaxRecipient';
        } else {
          singleValidInputFieldRecipient = 'hasSingleValidEmailRecipient';
        }
      }
      const recipients = multipleProviderRecipients.map(recipient => recipient.recipient);
      const faxNumberOrEmail = (isValidFaxNumber ? formatValidFaxNumber(trimmedInput, 'Domestic') : trimmedInput);
      this.setState({
        providerRecipientValue: newValue,
        hasSingleValidEmailRecipient: false,
        hasSingleValidFaxRecipient: false,
      });
      if (recipients.includes(faxNumberOrEmail)) {
        this.setState({
          isProviderValid: true,
          hasDuplicateRecipient: true,
        });
        return;
      }
      if (!isValidFaxNumberOrEmail) {
        // Cancel pending medsender check when input is invalid or cleared
        this.debouncedCheckMedsenderUser.cancel();
        if (trimmedInput) {
          this.setState({
            isProviderValid: false,
            hasDuplicateRecipient: false,
          });
        }
        return;
      }
      this.setState((prevState) => {
        const faxNumberValid = validFaxNumber(newValue).valid;
        const signatureRequired = faxNumberValid ? false : prevState.signatureRequired;
        return {
          signatureRequired,
          isProviderValid: true,
          [singleValidInputFieldRecipient]: true,
        };
      });

      // Check if recipient is a medsender user (debounced with leading edge)
      if (isValidFaxNumberOrEmail) {
        this.debouncedCheckMedsenderUser(newValue);
      }
    }
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested = () => {
    // Clear suggestions
    // TODO: add support for when user focuses away from input to cache
    // suggestions
    // NOTE: we don't clear suggestions for distribution lists as we're managing them locally
    if (!this.state.isDistributionList) { this.setState({ recipients: [] }); }
  };

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. If the suggestion selected is a fax number, It populates
  // recipient, org. and contact. If its an email address, it populates only the recipient
  getSuggestionValue = (suggestion) => {
    const { isPatient, isDistributionList } = this.state;
    if (isPatient) {
      const parsed = parsePatientName(suggestion.name);
      const nameValid = parsed.firstName !== '' && parsed.lastName !== '';

      this.setState((prevState) => {
        return {
          patientValue: suggestion.recipient,
          patientDOBValid: validDOB(suggestion.dob),
          patientNameValid: nameValid,
          patient: {
            ...prevState.patient,
            dob: suggestion.dob,
            firstName: parsed.firstName,
            middleName: parsed.middleName,
            lastName: parsed.lastName,
            recipient: suggestion.recipient,
          },
        };
      });
      return suggestion.recipient;
    } else if (isDistributionList) {
      this.setState({
        distributionListValue: suggestion.recipient,
        distributionListSlug: suggestion.slug,
      });
      return suggestion.recipient;
    }
    // If  valid number, then change display type to domestic. e.g (919) 818-8181
    const { valid: faxNumberValid, rawNumber: recipient } = validFaxNumber(suggestion.recipient);

    this.recordInfoFormRef.current?.setFieldsValue({
      ...(!this.state.org && suggestion.organization && { org: suggestion.organization }),
      ...(!this.state.contact && suggestion.name && { contact: suggestion.name }),
    });

    this.setState({
      org: suggestion.organization,
      contact: suggestion.name,
    });
    return faxNumberValid ? formatValidFaxNumber(recipient, 'Domestic') : suggestion.recipient;
  };

  // Gets suggestion of a particular section e.g users, organizations
  getSectionSuggestions(section) {
    return section.recipients;
  }

  // Fetches suggestions after hititing search api endpoint
  loadSuggestions(value) {
    if (this.state.isPatient) {
      this.props.searchRecipient('patient', value);
    } else {
      this.props.searchRecipient('provider', value);
    }
  }

  // Cancel button on dialog
  cancel = () => {
    const { defaultFaxNumber } = this.props;
    // Cancel pending debounced check to prevent stale callbacks after dialog closes
    this.debouncedCheckMedsenderUser.cancel();
    this.setState({
      searchText: '',
      org: '',
      contact: '',
      value: '',
      patientValue: '',
      patient: {
        recipient: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
      },
      patientRecipientValid: false,
      patientDOBValid: false,
      patientNameValid: false,
      faxNumberValid: false,
      emailValid: false,
      isChecked: false,
      signatureRequired: false,
      documentType: 'medical_record',
      callerId: defaultFaxNumber,
      showRemoveCoversheetCheckbox: false,
      providerRecipientValue: '',
      multipleProviderRecipients: [],
      hasDuplicateRecipient: false,
      isProviderValid: true,
      hasSingleValidEmailRecipient: false,
      hasSingleValidFaxRecipient: false,
      disableSecureReplies: false,
    });

    // Closes the send record maodal
    this.props.handleCancel(false);
    // TODO (Ammar Khan)
    // handle canceling of state in component and redux store
    // and then send to parent cancel function
  }

  runSubmit = async () => {
    const {
      org,
      contact,
      value,
      isChecked,
      emailValid,
      faxNumberValid,
      isPatient,
      isDistributionList,
      patient,
      documentType,
      memo,
      subject,
      callerId,
      signatureRequired,
      removeCoversheet,
      providerRecipientValue,
      isProviderValid,
      hasDuplicateRecipient,
      allowPatientEdits,
      disableSecureReplies,
      scheduledDateTime,
      distributionListSlug,
      distributionListValue,
    } = this.state;

    const { handleSubmit, defaultFaxNumber, associatedFaxNumbers } = this.props;

    const patientData = {
      ...patient,
      dob: formatDate(patient.dob),
      signatureRequired,
    };

    const firstNumber = associatedFaxNumbers[0] && associatedFaxNumbers[0][0];

    const recipient = faxNumberValid ? validFaxNumber(value).rawNumber : value;
    // adding in the input field value to multipleProviderRecipients if it's valid
    const inputFieldValueIsValid = isProviderValid && !hasDuplicateRecipient && providerRecipientValue;
    if (inputFieldValueIsValid && !isPatient && !isDistributionList) { await this.addProviderRecipient() }
    // updated in addProviderRecipient called above so destructured here instead of the beginning of the function
    const { multipleProviderRecipients } = this.state;

    const callerIdValue = callerId || firstNumber || null;

    // Reset the form before awaiting the send: every handleSubmit arg above is
    // already captured in a local, so clearing state here can't affect the send.
    // Doing it first preserves the optimistic field-clear UX and avoids a
    // post-unmount setState when the parent closes the dialog on completion.
    this.setState({
      org: '',
      contact: '',
      faxNumberValid: false,
      emailValid: false,
      patientRecipientValid: false,
      patientNameValid: false,
      patientDOBValid: false,
      isPatient: false,
      isDistributionList: false,
      signatureRequired: false,
      patient: {
        recipient: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
      },
      value: '',
      memo: '',
      documentType: 'medical_record',
      subject: '',
      callerId: defaultFaxNumber,
      scheduledDateTime: null,
      removeCoversheet: false,
      showRemoveCoversheetCheckbox: false,
      multipleProviderRecipients: [],
      providerRecipientValue: '',
      hasDuplicateRecipient: false,
      isProviderValid: true,
      allowPatientEdits: true,
      disableSecureReplies: false,
      distributionListSlug: null,
      distributionListValue: '',
      filteredDistributionLists: [],
    });

    // Await the send so the single-flight submit guard holds for the full request
    // duration — not just until local state resets. Otherwise a second fax/email could
    // be queued while the first is still in flight.
    await handleSubmit(
      org,
      contact,
      callerIdValue,
      isChecked,
      emailValid,
      isPatient,
      patientData,
      documentType,
      memo,
      subject,
      signatureRequired,
      removeCoversheet,
      multipleProviderRecipients,
      allowPatientEdits,
      disableSecureReplies,
      scheduledDateTime,
      isDistributionList ? { slug: distributionListSlug, name: distributionListValue } : null
    );
  }

  // Updates the state with the field value
  handleInputChange = (event) => {
    const change = {
      [event.target.name]: event.target.value,
    };
    this.setState(change);
  }

  handleDateChange = (_, dateString) => {
    this.setState({ patient: { ...this.state.patient, dob: dateString } });
  }

  patientMeta = (data, e) => {
    // Write e.target.value to variable since target will be set to null on a released/nullified synthetic event. https://fb.me/react-event-pooling
    const value = e.target.value;
    switch (data) {
      case 'recipient':
        this.setState((prevState) => {
          return {
            patientRecipientValid: validPatientRecipient(value),
            patient: {
              ...prevState.patient,
              recipient: value,
            },
          };
        });
        break;
      case 'dob': {
        const valid = validDOB(value);
        this.setState((prevState) => {
          return {
            patientDOBValid: valid,
            patient: { ...prevState.patient, dob: value },
          };
        });
        break;
      }
      case 'firstName':
        this.setState((prevState) => {
          const firstName = value;
          const { lastName } = prevState.patient;
          return {
            patientNameValid: firstName !== '' && lastName !== '',
            patient: { ...prevState.patient, firstName },
          };
        });
        break;
      case 'middleName':
        this.setState((prevState) => {
          return {
            patient: { ...prevState.patient, middleName: value },
          };
        });
        break;
      case 'lastName':
        this.setState((prevState) => {
          const lastName = value;
          const { firstName } = prevState.patient;
          return {
            patientNameValid: firstName !== '' && lastName !== '',
            patient: { ...prevState.patient, lastName },
          };
        });
        break;
    }
  }

  // Helper method to check if patient name is valid
  isPatientNameValid = () => {
    const { patient } = this.state;
    return patient.firstName && patient.firstName.trim() !== ''
      && patient.lastName && patient.lastName.trim() !== '';
  }

  // Check if the recipient email or fax number is valid
  validInput() {
    const { isPatient } = this.state;
    if (isPatient) {
      // eslint-disable-next-line object-curly-newline
      const { patientRecipientValid, patient } = this.state;
      const isDobValid = patient.dob && !(Number.isNaN(new Date(patient.dob).getTime()));
      const dataValid = isDobValid && patientRecipientValid && this.isPatientNameValid();
      return dataValid;
    }
    return false;
  }

  displaySuggestionText = (suggestion) => {
    const contactName = suggestion.name;
    const organizationName = suggestion.organization;
    const recipient = validFaxNumber(suggestion.recipient).valid ? formatValidFaxNumber(suggestion.recipient, 'Domestic') : suggestion.recipient;
    if (organizationName && contactName) return `${contactName} (${organizationName}) - ${recipient}`;
    if (!contactName && !organizationName) return `${recipient}`;
    if (!contactName && organizationName) return `${organizationName} - ${recipient}`;
    return `${contactName} - ${recipient}`;
  }

  handleActive = async (tab) => {
    if (tab === 'Patient') {
      // Cancel pending medsender check when switching away from Provider tab
      this.debouncedCheckMedsenderUser.cancel();
      this.setState({
        isPatient: true,
        isDistributionList: false
      });
      // Focus on patient field.
      if (this.patientInput) {
        setTimeout(() => {
          this.patientInput.focus();
        }, 100);
      }
    }
    else if (tab === 'Distribution List') {
      // Cancel pending medsender check when switching away from Provider tab
      this.debouncedCheckMedsenderUser.cancel();
      this.setState({
        isDistributionList: true,
        isPatient: false,
        isLoadingDistributionLists: !this._isDistributionListRequestInProgress
      });
      
      // Only fetch if there's no request in progress
      if (!this._isDistributionListRequestInProgress) {
        this._isDistributionListRequestInProgress = true;
        
        try {
          await this.props.getDistributionLists(this.props.currentOrganizationId);
          
          // Transform distribution lists to the required format by the Autosuggest component
          const formattedLists = [{
            recipients: this.props.distributionLists.map(list => ({
              recipient: list.name,
              slug: list.slug,
              departments: list.departments.map(department => department.name)
            }))
          }];
          
          this.setState({
            formattedDistributionLists: formattedLists,
            isLoadingDistributionLists: false
          });
        } catch (error) {
          this.setState({ 
            distributionListsError: 'Failed to load distribution lists. Please try again.',
            isLoadingDistributionLists: false
          });
        } finally {
          // Always reset the flag when done
          this._isDistributionListRequestInProgress = false;
        }
      }
      
      // Focus on distribution list field.
      if (this.distributionListInput) {
        setTimeout(() => {
          this.distributionListInput.focus();
        }, 100);
      }
    }
    else {
      this.setState({ 
        isPatient: false,
        isDistributionList: false
      });
      // Focus on provider field.
      if (this.input) {
        setTimeout(() => {
          this.input.focus();
        }, 100);
      }
    }
  }

  // Only shows suggestion when input length > 0 for distribution lists, 3 or more for others
  shouldRenderSuggestions = value => {
    const trimmed = value.trim();
    // distribution lists are managed locally so we can afford shorter input
    if (this.state.isDistributionList) {
      return trimmed.length > 0 && 
             this.state.formattedDistributionLists?.[0]?.recipients?.some(list => 
               list.recipient.toLowerCase().includes(trimmed.toLowerCase())
             );
    }
    
    return trimmed.length > 2;
  }

  // Styles the render suggestions component.
  renderSuggestion = suggestion => (
    <div>
      {this.displaySuggestionText(suggestion)}
    </div>
  );

  // Renders the section of the search results
  renderSectionTitle = (section) => {
    return (
      <strong>{section.title}</strong>
    );
  }

  isMedsenderUser = (faxNumberOrEmail) => {
    return axios.get(`${window.location.origin}/api/v1/records/medsender_user?sender=${faxNumberOrEmail}`).then(response => response.data.is_medsender_user);
  }

  addProviderRecipient = async () => {
    const {
      providerRecipientValue,
      multipleProviderRecipients,
      contact,
      org,
    } = this.state;
    // check to see if providerRecipientValue is valid
    if (providerRecipientValue.trim() !== '') {
      const isValidFaxNumber = validFaxNumber(providerRecipientValue).valid;
      const isValidEmail = validSendEmail(providerRecipientValue);
      const isValidFaxNumberOrEmail = isValidEmail || isValidFaxNumber;
      let recipientType = 'email';
      if (isValidFaxNumber) {
        recipientType = 'fax';
      }
      // newRecipientObject is an object passed in to multipleProviderRecipients and contains the following attributes, recipient, isValid, recipientType
      // recipientType could be either email, fax number or invalid
      const recipients = multipleProviderRecipients.map(recipient => recipient.recipient);
      const faxNumberOrEmail = (isValidFaxNumber ? formatValidFaxNumber(providerRecipientValue, 'Domestic') : providerRecipientValue);
      if (recipients.includes(faxNumberOrEmail.trim())) {
        this.setState({
          hasDuplicateRecipient: true,
        });
      } else if (!isValidFaxNumberOrEmail) {
        this.setState({
          hasDuplicateRecipient: false,
          isProviderValid: false,
        });
      } else {
        const isMedsenderUser = await this.isMedsenderUser(faxNumberOrEmail);
        const newRecipientObject = { recipient: faxNumberOrEmail, recipientType, isMedsenderUser };

        // Add contact/org info there's a single recipient in multipleProviderRecipients list
        if (multipleProviderRecipients.length < 1) {
          newRecipientObject.contact = contact;
          newRecipientObject.org = org;
        }

        const newMultipleRecipients = [...multipleProviderRecipients, newRecipientObject];
        // multipleProviderRecipients is an array of newRecipientObjects
        this.setState({
          multipleProviderRecipients: [...newMultipleRecipients],
          providerRecipientValue: '',
          hasDuplicateRecipient: false,
          isProviderValid: true,
          hasSingleValidEmailRecipient: false,
          hasSingleValidFaxRecipient: false,
          contact: newRecipientObject.contact || '',
          org: newRecipientObject.org || '',
        });
      }
    }
  }

  removeRecipient = (_, index) => {
    const { multipleProviderRecipients } = this.state;
    const newRecipientArray = [...multipleProviderRecipients];
    newRecipientArray.splice(index, 1);

    // If sender has removed multiple recipients, and a single recipient is remaining
    // Check the contact and org info for that first recipient
    if (multipleProviderRecipients.length === 2) {
      return this.setState({
        multipleProviderRecipients: [...newRecipientArray],
        contact: newRecipientArray[0].contact || '',
        org: newRecipientArray[0].org || '',
      });
    }
    return this.setState({
      multipleProviderRecipients: [...newRecipientArray],
    });
  }

  renderProviderInputComponent = (inputProps) => {
    const {
      multipleProviderRecipients,
      providerRecipientValue,
      hasDuplicateRecipient,
      isProviderValid,
      senderContactReply,
    } = this.state;
    
    let recipientsHasMedsenderUser = false;
    multipleProviderRecipients.forEach((recipient) => {
      if (recipient.isMedsenderUser) {
        recipientsHasMedsenderUser = true;
      }
    });
    return (
      <>
        <div style={{ display: 'flex', color: 'red' }}>
          {!isProviderValid ? 'Invalid Fax Number or Email' : ''}
        </div>
        <div style={{ display: 'flex', color: 'red' }}>
          {hasDuplicateRecipient ? 'Duplicate recipient already added' : ''}
        </div>
        <div style={{ display: 'flex' }}>
          <Input
            allowClear
            placeholder="Recipient Name, Fax Number or Email Address"
            onPressEnter={() => this.addProviderRecipient()}
            name="contact"
            maxLength={250}
            value={providerRecipientValue}
            style={{ marginTop: 10 }}
            {...inputProps}
            data-tutorial="send-recipient"
          />
          <Button onClick={() => this.addProviderRecipient()} type="primary" shape="circle" style={{ marginLeft: '10px', marginTop: '9px' }} icon={<PlusCircleOutlined />} />
        </div>
        <br />
        {multipleProviderRecipients.map((recipientObj, index) => (
          <Tag icon={recipientObj.isMedsenderUser ? <ThunderboltOutlined /> : ''}
            color={recipientObj.isMedsenderUser ? "purple" : "blue"}
            key={`${recipientObj.recipient}${index}`}
            closable
            onClose={() => this.removeRecipient(recipientObj, index)}
          >
            {recipientObj.recipient}
          </Tag>
        ))}
        <br />
      </>
    );
  }

  renderInputComponent = (inputProps) => {
    const {
      providerRecipientValue,
      isPatient,
      isDistributionList
    } = this.state;

    // Get appropriate placeholder from the map
    let placeholder = "Recipient Name, Fax Number or Email Address"; // default
    if (isPatient) {
      placeholder = this.placeholderMap.patient;
    } else if (isDistributionList) {
      placeholder = this.placeholderMap.distributionList;
    }
    
    return (
      <>
        <div style={{ display: 'flex' }}>
          <Input
            placeholder={placeholder}
            onChange={e => this.setState({ providerRecipientValue: e.target.value })}
            onPressEnter={() => this.addProviderRecipient()}
            name="contact"
            value={providerRecipientValue}
            style={{ marginTop: 10 }}
            {...inputProps}
            data-tutorial="send-recipient"
          />
        </div>
        {/* If there are no distribution lists, or the user is not on a distribution list, show the br */}
        {(!isDistributionList || this.props.distributionLists.length > 0) && <br />}
        {isDistributionList && !this.state.isLoadingDistributionLists && this.props.distributionLists.length === 0 && (
          <div style={{ 
            color: '#f5222d', 
            fontSize: '14px',
            marginBottom: '15px'
          }}>
            You don't have any distribution lists available. Please contact an administrator to add one.
          </div>
        )}
        {isDistributionList && this.state.isLoadingDistributionLists && (
          <div style={{ marginTop: '10px' }}>
            <Spin size="small" /> Loading distribution lists...
          </div>
        )}
        {this.state.distributionListsError && (
          <div style={{ 
            color: '#f5222d', 
            fontSize: '14px',
            marginBottom: '15px'
          }}>
            {this.state.distributionListsError}
          </div>
        )}
      </>
    );
  }

  checkRequireSignature = (checked) => {
    // due to adding the checkbox for preventing users from editing, we want to ensure if signature is required, edits are allowed
    this.setState({ signatureRequired: checked });
    if (checked) {
      this.setState({
        allowPatientEdits: true,
      });
    }
  }

  addSingleRecipient = (faxNumberOrEmail, recipientType, isMedsenderUser) => {
    // Use functional setState to ensure we have the latest state and prevent duplicates
    this.setState((prevState) => {
      const { multipleProviderRecipients } = prevState;
      const recipients = multipleProviderRecipients.map(r => r.recipient);
      // Check for duplicate using latest state to prevent race condition
      if (recipients.includes(faxNumberOrEmail)) {
        return null; // Don't update state if already exists
      }
      const newRecipientObject = { recipient: faxNumberOrEmail, recipientType, isMedsenderUser };
      return {
        multipleProviderRecipients: [...multipleProviderRecipients, newRecipientObject],
        providerRecipientValue: '',
      };
    });
  }

  checkIfMedsenderUser = async (faxNumberOrEmail, recipientType) => {
    const isMedsenderUser = await this.isMedsenderUser(faxNumberOrEmail);
    // Read state AFTER await to get the latest value and prevent race condition
    const { multipleProviderRecipients } = this.state;
    const recipients = multipleProviderRecipients.map(recipient => recipient.recipient);
    if (recipients.includes(faxNumberOrEmail.trim())) {
      return isMedsenderUser
    }
    if (isMedsenderUser) {
      notification.info({
        message: 'Medsender User Detected!',
        description:
          'We offer a variety of perks when sending a record to a medsender user, such as guaranteed instant delivery,  gives recipients the ability to search through the record, and the ability to message the sender/recipient via Medsender Connect Chat!',
      });
      this.addSingleRecipient(faxNumberOrEmail, recipientType, isMedsenderUser);
    }
    return isMedsenderUser
  }

  // Method to check medsender user status on input change
  // Accepts inputValue parameter to ensure we use the latest value (since setState is async)
  checkMedsenderUserOnInput(inputValue) {
    const { multipleProviderRecipients } = this.state;
    const trimmedInput = (inputValue || '').trim();
    if (!trimmedInput) return;

    const isValidFaxNumber = validFaxNumber(trimmedInput).valid;
    const isValidEmail = validSendEmail(trimmedInput);
    const isValidFaxNumberOrEmail = isValidEmail || isValidFaxNumber;

    if (isValidFaxNumberOrEmail) {
      const faxNumberOrEmail = isValidFaxNumber
        ? formatValidFaxNumber(trimmedInput, 'Domestic')
        : trimmedInput;
      const recipientType = isValidFaxNumber ? 'fax' : 'email';

      // Check if already in recipients list
      const recipients = multipleProviderRecipients.map((recipient) => recipient.recipient);
      if (!recipients.includes(faxNumberOrEmail)) {
        this.checkIfMedsenderUser(faxNumberOrEmail, recipientType);
      }
    }
  }

  isValidProviderInput = (hasNoOrInvalidLength) => {
    const { isPatient } = this.state;
    if (isPatient) {
      return false
    } else if (!hasNoOrInvalidLength){
      // Note: medsender user check is now handled via debounced callback on input change
      // to prevent excessive API calls during render
      return false
    }
    return hasNoOrInvalidLength
  }
  
  renderModalActionButton = (hasNoOrInvalidLength) => {
    const { isPatient, patient, isDistributionList, distributionListSlug } = this.state;
    const { patientDOB, patientFirstName, patientMiddleName, patientLastName } = this.props;

    // Normalize DOB for comparison to handle different date formats
    const normalizeDOB = (dob) => {
      if (!dob) return '';
      const momentDate = moment(dob);
      return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : '';
    };

    const patientInformationChanged = normalizeDOB(patient.dob) !== normalizeDOB(patientDOB)
      || (patient.firstName || '') !== (patientFirstName || '')
      || (patient.middleName || '') !== (patientMiddleName || '')
      || (patient.lastName || '') !== (patientLastName || '');
    const nameAndDobPresent = patientFirstName && patientLastName && patientDOB;
    if (isPatient && (patientInformationChanged || !nameAndDobPresent)) {
      return (
        <Popconfirm
          title="Patient information you've entered is different from we have on file. Send and update patient information?"
          disabled={!this.validInput()}
          onConfirm={this.submit.bind(this)}
          cancelText="Cancel"
          okText="Send and Update"
        >
          <Button
            type="primary"
            style={{ marginLeft: 8 }}
            data-tutorial="send-submit"
            // disabled={!this.validInput()}            disabled={this.validInput() ? false : (isPatient || hasNoOrInvalidLength)}
            disabled={this.validInput() ? false : (isPatient || hasNoOrInvalidLength)}
          >
            Send
          </Button>
        </Popconfirm>
      );
    }
    // Enable the button if a distribution list is selected
    if (isDistributionList) {
      return (
        <Button
          type="primary"
          style={{ marginLeft: 8 }}
          data-tutorial="send-submit"
          disabled={!distributionListSlug}
          onClick={this.submit.bind(this)}
        >
          Send
        </Button>
      );
    }
    
    return (
      <Button
        type="primary"
        style={{ marginLeft: 8 }}
        data-tutorial="send-submit"
        disabled={this.validInput() ? false : this.isValidProviderInput(hasNoOrInvalidLength)}
        onClick={this.submit.bind(this)}
      >
        Send
      </Button>
    );
  }

  render() {
    const {
      open,
      recipients,
      patientRecipientValid,
      patientNameValid,
      org,
      contact,
      value,
      patientValue,
      distributionListValue,
      patient,
      isPatient,
      memo,
      callerId,
      removeCoversheet,
      showRemoveCoversheetCheckbox,
      multipleProviderRecipients,
      providerRecipientValue,
      hasDuplicateRecipient,
      isProviderValid,
      hasSingleValidEmailRecipient,
      hasSingleValidFaxRecipient,
      allowPatientEdits,
      signatureRequired,
      noReplyInformationError,
      scheduleFaxForLater,
      scheduledDateTime,
      scrollAtBottom,
      isDistributionList,
    } = this.state;

    // Check if multiple records are selected (multi-send mode)
    const { selectedRecords } = this.props;
    const isMultiSelect = selectedRecords && selectedRecords.length > 1;
    const recordCount = selectedRecords ? selectedRecords.length : 1;

    const validEmailRecipients = multipleProviderRecipients.filter(recipient => recipient.recipientType === 'email');
    const validFaxRecipients = multipleProviderRecipients.filter(recipient => recipient.recipientType === 'fax');
    const invalidLength = (multipleProviderRecipients.length > 25);
    const validProviderRecipientFieldValue = providerRecipientValue && !hasDuplicateRecipient && isProviderValid;
    const hasNoOrInvalidLength = ((!validProviderRecipientFieldValue && multipleProviderRecipients.length === 0) || invalidLength);

    // Autosuggest will pass through all these props to the input.
    const inputProps = {
      value: providerRecipientValue,
      onChange: this.onChange,
      autoFocus: true, // Focus on provider field once component is rendered.
    };

    const inputPatientProps = {
      value: patientValue,
      onChange: this.onChange,
    };

    const inputDistributionListProps = {
      value: distributionListValue,
      onChange: this.onChange,
    };

    const {
      cancel,
      submit,
      patientMeta,
      renderSectionTitle,
      renderSuggestion,
      onSuggestionsFetchRequested,
      onSuggestionsClearRequested,
      getSuggestionValue,
      shouldRenderSuggestions,
      getSectionSuggestions,
      handleActive,
    } = this;

    const { userCanSendFaxRecordsOnly } = this.props;

    let displayConfirmationDialogBeforeSending;

    if (isPatient) {
      displayConfirmationDialogBeforeSending = patientRecipientValid && this.isPatientNameValid() && patient.dob;
    } else {
      displayConfirmationDialogBeforeSending = !hasNoOrInvalidLength;
    }

    const { searchLoading, associatedFaxNumbers: faxNumbers } = this.props;

    // The document type will be displayed in the dialog text. The _ is replaced
    // because the default type is 'medical_record' => 'medical record' after
    // being replaced
    const documentType = this.state.documentType.replace('_', ' ');
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const isPatientDobValid = patient.dob && !(Number.isNaN(new Date(patient.dob).getTime()));
    const showScrollToBottomIcon = !scrollAtBottom && open && !isPatient && !isDistributionList;
    return (
      <>
      {showScrollToBottomIcon && (
        <Tooltip title="Scroll to bottom">
          <DownCircleOutlined 
            style={{
              position: 'fixed',
              left: this.state.iconPosition?.x,
              top: this.state.iconPosition?.y,
              zIndex: 1001,
              fontSize: '24px',
              color: '#1890ff',
              cursor: 'pointer',
              backgroundColor: 'white',
              borderRadius: '50%',
              padding: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
            className='scroll-to-bottom-icon'
            onClick={() => {
              this.scrollToBottom()
            }}
          />
        </Tooltip>
      )}
      <DraggableModal
        className='send-records-modal'
        footer={[
          <Button onClick={cancel}>Cancel</Button>,
          this.renderModalActionButton(hasNoOrInvalidLength),
        ]}
        visible={open}
        handleClass='.ant-tabs-nav'
        contentStyle={customContentStyle}
        width={800}
        onCancel={cancel}
        onDrag={this.setScrollButtonPosition}
      >
        <Tabs defaultActiveKey="Provider" onChange={handleActive}>
          <TabPane tab={isMultiSelect ? `Provider (${recordCount} records)` : "Provider"} key="Provider">
            <div style={sendContainer}>
              {isMultiSelect && (
                <Alert
                  style={{ marginBottom: 10 }}
                  message={`You are about to send ${recordCount} records to the same recipient(s).`}
                  type="info"
                  showIcon
                />
              )}
              {
                noReplyInformationError
                && (
                  <Alert
                    style={{ marginBottom: 10 }}
                    message={(
                      <div>
                        You cannot reply to a record that you sent or someone in your organization sent to themselves
                      </div>
                    )}
                    type="error"
                    showIcon
                  />
                )
              }
              <p>Enter the Recipient(s)</p>
              <p style={{ margin: 0, fontSize: 15 }}>
                <b>
                  Type any fax number, email, or organization/person name
                </b>
              </p>
              <div style={inputBox}>
                <Autosuggest
                  multiSection={true}
                  suggestions={recipients}
                  onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                  onSuggestionsClearRequested={onSuggestionsClearRequested}
                  getSuggestionValue={getSuggestionValue}
                  renderSuggestion={renderSuggestion}
                  inputProps={inputProps}
                  renderInputComponent={this.renderProviderInputComponent}
                  renderSectionTitle={renderSectionTitle}
                  getSectionSuggestions={getSectionSuggestions}
                  theme={theme}
                  ref={this.storeInputReference}
                />
                {searchLoading && (
                  <Spin
                    style={{ right: '3%' }}
                  />
                )}
              </div>
              {(validFaxRecipients.length > 0 || validEmailRecipients.length > 0 || hasSingleValidFaxRecipient || hasSingleValidEmailRecipient) && (
                <>
                  <Form style={{ marginTop: 20 }} layout="vertical" ref={this.recordInfoFormRef}>
                    {multipleProviderRecipients.length < 2 && !((hasSingleValidFaxRecipient || hasSingleValidEmailRecipient) && multipleProviderRecipients.length === 1)
                      && (
                      <>
                        <Form.Item
                          hasFeedback
                          name="org"
                          label="Recipient Organization"
                          style={{ width: '600px' }}
                        >
                          <Input
                            placeholder="(optional) Organization Name"
                            onChange={this.handleInputChange}
                            name="org"
                            maxLength={100}
                            defaultValue={org !== '' ? org : ''}
                          />
                        </Form.Item>
                        <Form.Item
                          hasFeedback
                          name="contact"
                          label="Recipient Name"
                          style={{ width: '600px' }}
                        >
                          <Input
                            placeholder="(optional) Organization Contact"
                            onChange={this.handleInputChange}
                            maxLength={100}
                            name="contact"
                            defaultValue={contact !== '' ? contact : ''}
                          />
                        </Form.Item>
                      </>
                      )
                    }
                    {!validEmailRecipients.length > 0 && !hasSingleValidEmailRecipient
                      && (
                        <Form.Item
                          hasFeedback
                          name="memo"
                          label="Memo"
                          style={{ width: '600px' }}
                        >
                          <Input.TextArea
                            placeholder="(optional) Memo/Note"
                            onChange={this.handleInputChange}
                            name="memo"
                            defaultValue={memo !== '' ? memo : ''}
                            autoSize={{ minRows: 1, maxRows: 6 }}
                          />
                        </Form.Item>
                      )}
                  </Form>
                </>
              )}
              {((validEmailRecipients.length > 0 || hasSingleValidEmailRecipient)) && (
                <Form style={{ marginTop: 25 }} layout="vertical">
                  <Form.Item
                    hasFeedback
                    name="subject"
                    label="Subject"
                    style={{ width: '600px' }}
                    rules={[
                      () => ({
                        validator(_, subject) {
                          if (subject.length < 150) return Promise.resolve();
                          return Promise.reject('Subject cannot have more than 150 characters');
                        },
                      }),
                    ]}
                  >
                    <Input
                      placeholder="(optional) Subject"
                      onChange={this.handleInputChange}
                      name="subject"
                    />
                  </Form.Item>
                  <Form.Item
                    hasFeedback
                    name="memo"
                    label="Memo"
                    style={{ width: '600px' }}
                  >
                    <Input.TextArea
                      placeholder="(optional) Memo/Note"
                      onChange={this.handleInputChange}
                      name="memo"
                      autoSize={{ minRows: 1, maxRows: 6 }}
                      defaultValue={memo !== '' ? memo : ''}
                    />
                  </Form.Item>
                </Form>
              )}
              {((validFaxRecipients.length > 0 && faxNumbers.length > 0) || (hasSingleValidFaxRecipient && faxNumbers.length > 0)) && (
                <div>
                  <div style={{ display: 'flex', height: '30px' }}>
                    <p>
                      Send record with this Caller ID ...
                    </p>
                    <Tooltip
                      overlayStyle={{ zIndex: 1600, marginLeft: '10px' }}
                      title="Outgoing caller id for any fax transmission"
                    >
                      <QuestionCircleOutlined style={{ margin: '5px 0px 0px 5px' }} />
                    </Tooltip>
                  </div>
                  <Select
                    value={callerId || faxNumbers[0][0]}
                    onChange={number => this.setState({ callerId: number })}
                    style={dropDownStyle}
                  >
                    {faxNumbers.map(number => <Select.Option key={number[0]} value={number[0]}>{`${number[0]} - ${number[1]}`}</Select.Option>)}
                  </Select>
                </div>
              )}
              <div style={{ margin: '0 0 10px' }}>
                <p style={{ display: 'inline-block', marginTop: '7px' }}>
                  Allow the recipient to access this document for...
                </p>
                <Tooltip
                  overlayStyle={{ zIndex: 1600, marginLeft: '10px' }}
                  title="The recipient will be able to access the document for this amount of time"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
                <br />
                <Radio.Group
                  onChange={this.handleDocumentTypeChange}
                  value={this.state.documentType}
                  style={{ marginBottom: 20 }}
                >
                  <Radio value="medical_record" style={{ marginLeft: '10px' }}>30 days</Radio>
                  <Radio value="claim" style={{ marginLeft: '10px' }}>60 days</Radio>
                  {this.state.extendedDocumentAccessDurationsEnabled && (
                    <>
                      <Radio value="medical_record_90_days" style={{ marginLeft: '10px' }}>90 days</Radio>
                      <Radio value="medical_record_120_days" style={{ marginLeft: '10px' }}>120 days</Radio>
                    </>
                  )}
                </Radio.Group>
                <p>Additional options</p>
                <Checkbox
                  checked={this.state.isChecked}
                  onChange={(e) => {
                    this.setState({ isChecked: e.target.checked });
                  }}
                >
                  Allow the recipient to send this document to others
                </Checkbox>
                <br/>
                <Checkbox
                  style={{ margin: '10px 0px' }}
                  disabled={userCanSendFaxRecordsOnly}
                  onChange={(e) => this.checkRequireSignature(e.target.checked)}
                >
                  Require the recipient to sign this document before replying
                </Checkbox>
                {showRemoveCoversheetCheckbox
                  && (
                    <Checkbox
                      style={{ margin: '0 0 10px' }}
                      checked={removeCoversheet}
                      onChange={(e) => {
                        this.setState({ removeCoversheet: e.target.checked });
                      }}
                    >
                      Send record to recipients without Medsender cover page
                    </Checkbox>
                  )
                }
                <br />
                {(multipleProviderRecipients.length === 0 || !multipleProviderRecipients.every(recipient => recipient.isMedsenderUser)) &&
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Checkbox
                      style={{ margin: '0 0 10px' }}
                      checked={scheduleFaxForLater}
                      onChange={(e) => {
                        this.setState({ 
                          scheduleFaxForLater: e.target.checked,
                          scheduledDateTime: e.target.checked ? moment().toISOString() : null 
                        });
                      }}
                    >
                      Schedule for later
                    </Checkbox>
                    {scheduleFaxForLater && (
                      <>
                        <br />
                        <DatePicker
                          style={{ marginLeft: '20px', width: '175px' }}
                          showTime={{ format: 'HH:mm' }}
                          format="MM/DD HH:mm"
                          placeholder="Schedule Time"
                          value={scheduledDateTime ? moment(scheduledDateTime) : null}
                          disabledDate={current => current && current < moment().startOf('day')}
                          disabledTime={current => {
                            const now = moment();
                            const isSameDay = current && current.isSame(now, 'day');
                            
                            return {
                              disabledHours: () => isSameDay ? [...Array(now.hour())].map((_, i) => i) : [],
                              disabledMinutes: (hour) => isSameDay && hour === now.hour() ? [...Array(now.minute())].map((_, i) => i) : []
                            };
                          }}
                          onChange={(date) => {
                            const scheduledTime = date ? date.startOf('minute').toISOString() : null;
                            this.setState({ scheduledDateTime: scheduledTime });
                          }}
                        />
                      </>
                    )}
                  </div>
                }
                <br />
                {(validEmailRecipients.length > 0 || hasSingleValidEmailRecipient) && (
                  <Checkbox
                    style={{ margin: '0 0 10px' }}
                    checked={userCanSendFaxRecordsOnly ? true : allowPatientEdits}
                    disabled={signatureRequired}
                    onChange={(e) => {
                      this.setState({ allowPatientEdits: e.target.checked });
                    }}
                  >
                    Allow recipient to edit record
                  </Checkbox>
                )}
                {this.validInput() && value !== '' && (
                  <p style={{ color: 'red', textAlign: 'center' }}>
                    {this.state.inputError}
                  </p>
                )}
                {displayConfirmationDialogBeforeSending && (
                  <p>
                    Clicking &quot;Send&quot; will send {isMultiSelect ? `${recordCount} documents` : 'this document'}.
                    Only click if you are absolutely sure and you&apos;ve
                    made sure this is the correct recipient(s).
                    Please Note: You cannot send a record to more than 25 recipients at a time.
                  </p>
                )}
              </div>
            </div>
          </TabPane>
          <TabPane
            tab={isMultiSelect ? "Patient (single record only)" : "Patient"}
            key="Patient"
            disabled={isMultiSelect}
          >
            <div style={sendContainer}>
              <p>Enter the Patient&#39;s Info</p>
              <div style={inputBox}>
                <Autosuggest
                  multiSection={true}
                  suggestions={recipients}
                  onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                  onSuggestionsClearRequested={onSuggestionsClearRequested}
                  shouldRenderSuggestions={shouldRenderSuggestions}
                  getSuggestionValue={getSuggestionValue}
                  renderSuggestion={renderSuggestion}
                  inputProps={inputPatientProps}
                  renderInputComponent={this.renderInputComponent}
                  renderSectionTitle={renderSectionTitle}
                  getSectionSuggestions={getSectionSuggestions}
                  theme={theme}
                  ref={this.storePatientInputReference}
                />
                {searchLoading && <Spin size="medium" />}
              </div>
              {(patient.dob && patientRecipientValid && this.isPatientNameValid()) && (
                <Form style={{ marginTop: 25 }} layout="vertical">
                  <Form.Item
                    name="memo"
                    label="Memo"
                    style={{ width: '600px' }}
                  >
                    <Input.TextArea
                      placeholder="Memo/Note (optional)"
                      onChange={this.handleInputChange}
                      name="memo"
                      autoSize={{ minRows: 1, maxRows: 6 }}
                      defaultValue={memo !== '' ? memo : ''}
                    />
                  </Form.Item>
                </Form>
              )}
              <Form style={{ marginTop: 30 }} layout="vertical">
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item
                      hasFeedback
                      label="First Name"
                      initialValue={patient.firstName}
                      required
                      validateStatus={!patient.firstName || patient.firstName.trim() === '' ? 'error' : 'success'}
                      help={!patient.firstName || patient.firstName.trim() === '' ? 'First name is required' : ''}
                    >
                      <Input
                        placeholder="First name"
                        onChange={patientMeta.bind(this, 'firstName')}
                        value={patient.firstName}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Middle Name"
                      initialValue={patient.middleName}
                    >
                      <Input
                        placeholder="Middle name"
                        onChange={patientMeta.bind(this, 'middleName')}
                        value={patient.middleName}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      hasFeedback
                      label="Last Name"
                      initialValue={patient.lastName}
                      required
                      validateStatus={!patient.lastName || patient.lastName.trim() === '' ? 'error' : 'success'}
                      help={!patient.lastName || patient.lastName.trim() === '' ? 'Last name is required' : ''}
                    >
                      <Input
                        placeholder="Last name"
                        onChange={patientMeta.bind(this, 'lastName')}
                        value={patient.lastName}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  label="Date of Birth (MM/DD/YYYY)"
                  initialValue={isPatientDobValid ? moment(patient.dob, dateFormat) : ''}
                  required
                  validateStatus={!isPatientDobValid ? 'error' : 'success'}
                  help={!isPatientDobValid ? 'Date of birth is required' : ''}
                >
                  <DatePicker
                    popupStyle={{ zIndex: 1600 }}
                    onChange={this.handleDateChange}
                    format={dateFormat}
                    value={isPatientDobValid ? moment(patient.dob, dateFormat) : ''}
                  />
                </Form.Item>
              </Form>
              <div style={{ margin: '20px 0px 10px' }}>
                <p>Additional options</p>
                <Checkbox
                  style={{ margin: '10px 0px' }}
                  disabled={userCanSendFaxRecordsOnly}
                  onChange={(e) => {
                    this.setState({ signatureRequired: e.target.checked });
                  }}
                >
                  Require the recipient to sign this document before replying
                </Checkbox>
                <br />
                <Checkbox
                    style={{ margin: '0 0 10px' }}
                    onChange={(e) => {
                    this.setState({ disableSecureReplies: e.target.checked });
                  }}
                >
                  Don't allow the recipient to reply to this document
                </Checkbox>
                {displayConfirmationDialogBeforeSending && (
                  <p>
                    You are about to send a record to <b>{patient.recipient}</b>
                    . Clicking &quot;Send Record&quot; will send this
                    record. Only click if you are absolutely sure and
                    you&apos;ve made sure this is the correct recipient.
                  </p>
                )}
              </div>
            </div>
          </TabPane>
          <TabPane tab="Distribution List" key="Distribution List">
            <div style={sendContainer}>
              <h4 style={{ 
                fontSize: '16px', 
                marginBottom: '8px'
              }}>
                Distribution List
              </h4>
              <p style={{  
                fontSize: '14px',
                color: '#595959',
                maxWidth: '600px',
                marginBottom: '0px'
              }}>
                Send this document to fax numbers in a distribution list. Type below to search and select a distribution list by name. You can also specify a memo to be included in the coversheet we generate for you.
              </p>
              <div style={inputBox}>
                <Autosuggest
                  multiSection={true}
                  suggestions={this.state.filteredDistributionLists}
                  onSuggestionsFetchRequested={() => {}}
                  shouldRenderSuggestions={shouldRenderSuggestions}
                  getSuggestionValue={getSuggestionValue}
                  renderSuggestion={renderSuggestion}
                  inputProps={inputDistributionListProps}
                  renderInputComponent={this.renderInputComponent}
                  renderSectionTitle={renderSectionTitle}
                  getSectionSuggestions={getSectionSuggestions}
                  theme={theme}
                  ref={this.storeDistributionListInputReference}
                />
                {searchLoading && <Spin size="medium" />}
              </div>
              <div>
                <Form.Item
                  hasFeedback
                  name="memo"
                  label="Memo"
                  style={{ width: '600px' }}
                >
                  <Input.TextArea
                    placeholder="(optional) Memo/Note"
                    onChange={this.handleInputChange}
                    name="memo"
                    defaultValue={memo !== '' ? memo : ''}
                    autoSize={{ minRows: 4, maxRows: 6 }}
                  />
                </Form.Item>
                {(faxNumbers.length > 0) && (
                  <div>
                    <div style={{ display: 'flex', height: '30px' }}>
                      <p>
                        Send record with this Caller ID ...
                      </p>
                      <Tooltip
                        overlayStyle={{ zIndex: 1600, marginLeft: '10px' }}
                        title="Outgoing caller id for any fax transmission"
                      >
                        <QuestionCircleOutlined style={{ margin: '5px 0px 0px 5px' }} />
                      </Tooltip>
                    </div>
                    <Select
                      value={callerId || faxNumbers[0][0]}
                      onChange={number => this.setState({ callerId: number })}
                      style={dropDownStyle}
                    >
                      {faxNumbers.map(number => <Select.Option key={number[0]} value={number[0]}>{`${number[0]} - ${number[1]}`}</Select.Option>)}
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </TabPane>
        </Tabs>
      </DraggableModal>
      </>
    );
  }
}

SendRecordsDialog.defaultProps = {
  patientName: '',
  patientFirstName: '',
  patientMiddleName: '',
  patientLastName: '',
  patientDOB: '',
  isReplyButtonClicked: false,
  senderContactReply: '',
  selectedRecord: null,
  selectedRecords: [],
  senderOrganizationReply: '',
  senderNameReply: '',
  currentOrganizationId: null,
  distributionLists: [],
};

SendRecordsDialog.propTypes = {
  associatedFaxNumbers: PropTypes.array.isRequired,
  defaultFaxNumber: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  dataSource: PropTypes.array.isRequired,
  searchRecipient: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  patientName: PropTypes.string,
  patientFirstName: PropTypes.string,
  patientMiddleName: PropTypes.string,
  patientLastName: PropTypes.string,
  patientDOB: PropTypes.string,
  userCanSendFaxRecordsOnly: PropTypes.bool.isRequired,
  senderContactReply: PropTypes.string,
  isReplyButtonClicked: PropTypes.bool,
  currentOrganizationId: PropTypes.number,
  getDistributionLists: PropTypes.func.isRequired,
  distributionLists: PropTypes.array,
  selectedRecord: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  selectedRecords: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  senderOrganizationReply: PropTypes.string,
  senderNameReply: PropTypes.string,
};

export default connect(
  state => ({
    distributionLists: state.userProfile.distributionLists || [],
    departments: state.auth.departments || [],
  }),
  { getDistributionLists }
)(SendRecordsDialog);
